import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

function ClientSetup() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadInvitation = useCallback(async () => {
    try {
      // Extract token from URL
      const params = new URLSearchParams(location.search);
      const token = params.get('token');

      if (!token) {
        setError('Invalid or missing invitation link');
        setLoading(false);
        return;
      }

      // Validate invitation via secure Cloud Function
      // This prevents direct access to the invitations collection
      const validateInvitation = httpsCallable(functions, 'validateInvitation');
      const result = await validateInvitation({ token });

      if (result.data.valid && result.data.invitation) {
        setInvitation(result.data.invitation);
      } else {
        setError('Invalid invitation. Please contact your administrator.');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading invitation:', err);

      // Handle specific error codes from Cloud Function
      if (err.code === 'functions/not-found') {
        setError('Invitation not found. This link may be invalid.');
      } else if (err.code === 'functions/failed-precondition') {
        setError(err.message || 'This invitation has expired or already been used.');
      } else {
        setError('Failed to load invitation. Please try again or contact support.');
      }

      setLoading(false);
    }
  }, [location.search]);

  useEffect(() => {
    loadInvitation();
  }, [loadInvitation]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Password validation - must match server requirements
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[0-9]/.test(password)) {
      toast.error('Password must contain at least one number');
      return;
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      toast.error('Password must contain at least one special character');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    const loadingToast = toast.loading('Creating your account...');

    try {
      // Call Cloud Function to create admin account
      // Use email as displayName since we're not asking for a separate name
      const completeSetup = httpsCallable(functions, 'completeAdminSetup');
      await completeSetup({
        invitationId: invitation.id,
        displayName: invitation.email,
        password: password
      });

      toast.dismiss(loadingToast);
      toast.success('Account created successfully! Please log in with your credentials.');

      // Redirect to login page after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (err) {
      console.error('Error creating account:', err);
      toast.dismiss(loadingToast);

      if (err.code === 'functions/already-exists') {
        toast.error('An account with this email already exists. Please contact your administrator.');
      } else if (err.code === 'functions/not-found') {
        toast.error('Invitation not found or already used.');
      } else if (err.code === 'functions/failed-precondition') {
        toast.error(err.message || 'Unable to create account. Invitation may have expired.');
      } else {
        toast.error('Failed to create account. Please try again or contact support.');
      }
      setSubmitting(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '40px',
      maxWidth: '500px',
      width: '100%',
      boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
    },
    logo: {
      textAlign: 'center',
      marginBottom: '30px'
    },
    logoText: {
      fontSize: '32px',
      fontWeight: 'bold',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '10px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '10px',
      textAlign: 'center'
    },
    subtitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '30px',
      textAlign: 'center'
    },
    companyInfo: {
      backgroundColor: '#f3f4f6',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '30px'
    },
    companyName: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '12px'
    },
    infoRow: {
      fontSize: '14px',
      color: '#4b5563',
      marginBottom: '8px',
      display: 'flex',
      justifyContent: 'space-between'
    },
    label: {
      fontWeight: '500',
      color: '#6b7280'
    },
    value: {
      fontWeight: '600',
      color: '#1f2937'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    },
    inputLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151'
    },
    input: {
      padding: '12px 16px',
      fontSize: '16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      outline: 'none',
      transition: 'border-color 0.2s',
      fontFamily: 'inherit'
    },
    button: {
      padding: '16px 32px',
      fontSize: '18px',
      fontWeight: '700',
      color: '#ffffff',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      marginTop: '10px',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
      letterSpacing: '0.5px'
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    errorContainer: {
      textAlign: 'center'
    },
    errorIcon: {
      fontSize: '48px',
      marginBottom: '20px'
    },
    errorTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#dc2626',
      marginBottom: '10px'
    },
    errorMessage: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '20px'
    },
    backButton: {
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#667eea',
      backgroundColor: 'transparent',
      border: '2px solid #667eea',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    spinner: {
      border: '3px solid #f3f4f6',
      borderTop: '3px solid #667eea',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      animation: 'spin 1s linear infinite',
      margin: '0 auto'
    },
    loadingText: {
      textAlign: 'center',
      color: '#6b7280',
      marginTop: '20px',
      fontSize: '14px'
    },
    helpText: {
      fontSize: '12px',
      color: '#6b7280',
      marginTop: '4px'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <div style={styles.logoText}>SkipEye</div>
          </div>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>Loading invitation...</div>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <div style={styles.logoText}>SkipEye</div>
          </div>
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>⚠️</div>
            <div style={styles.errorTitle}>Invalid Invitation</div>
            <div style={styles.errorMessage}>{error}</div>
            <button
              style={styles.backButton}
              onClick={() => navigate('/')}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#667eea';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#667eea';
              }}
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <div style={styles.logoText}>SkipEye</div>
        </div>

        <div style={styles.title}>Welcome to SkipEye!</div>
        <div style={styles.subtitle}>Complete your account setup to get started</div>

        <div style={styles.companyInfo}>
          <div style={styles.companyName}>{invitation.companyName}</div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Account email:</span>
            <span style={styles.value}>{invitation.email}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Driver accounts:</span>
            <span style={styles.value}>{invitation.maxDrivers} included</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Trial period:</span>
            <span style={styles.value}>{invitation.trialDays} days</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>Create Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="Enter your password"
              required
              disabled={submitting}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
            {/* Password requirements checklist */}
            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Password requirements:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <span style={{ color: password.length >= 8 ? '#10b981' : '#9ca3af', fontWeight: '600' }}>
                    {password.length >= 8 ? '✓' : '○'}
                  </span>
                  <span style={{ color: password.length >= 8 ? '#065f46' : '#6b7280' }}>
                    At least 8 characters {password.length > 0 && `(${password.length}/8)`}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <span style={{ color: /[A-Z]/.test(password) ? '#10b981' : '#9ca3af', fontWeight: '600' }}>
                    {/[A-Z]/.test(password) ? '✓' : '○'}
                  </span>
                  <span style={{ color: /[A-Z]/.test(password) ? '#065f46' : '#6b7280' }}>
                    At least 1 uppercase letter (A-Z)
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <span style={{ color: /[0-9]/.test(password) ? '#10b981' : '#9ca3af', fontWeight: '600' }}>
                    {/[0-9]/.test(password) ? '✓' : '○'}
                  </span>
                  <span style={{ color: /[0-9]/.test(password) ? '#065f46' : '#6b7280' }}>
                    At least 1 number (0-9)
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <span style={{ color: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? '#10b981' : '#9ca3af', fontWeight: '600' }}>
                    {/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? '✓' : '○'}
                  </span>
                  <span style={{ color: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? '#065f46' : '#6b7280' }}>
                    At least 1 special character (!@#$%^&* etc.)
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              placeholder="Re-enter your password"
              required
              disabled={submitting}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
            {/* Password match indicator */}
            {confirmPassword.length > 0 && (
              <div style={{
                marginTop: '8px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: password === confirmPassword ? '#065f46' : '#dc2626'
              }}>
                <span style={{ fontWeight: '600' }}>
                  {password === confirmPassword ? '✓' : '✗'}
                </span>
                {password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
              </div>
            )}
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(submitting ? styles.buttonDisabled : {})
            }}
            disabled={submitting}
            onMouseOver={(e) => {
              if (!submitting) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {submitting ? 'Creating Account...' : 'Complete Setup'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ClientSetup;
